import SceneObject from "../../../lib/services/scene/sceneobject";
import Puppy from "./puppy";
import InputService from "../../../lib/services/input/inputservice";
import Stage from "../../../lib/services/stage/stage";
import DioramaCamera from "../../../lib/services/scene/diorama_camera";
import Reticle from "../reticle/reticle";
import { find_reticle_hit } from "../../../lib/services/collision/raycast_helper";
import RenderService from "../../../lib/services/render/render_service";
import CollisionService from "../../../lib/services/collision/collision_service";
import { ReticleHit } from "../../../lib/services/collision/raycast_helper";
import { PuppyClips } from "./puppyclips";
import { GROUND_NODE_ID, PuppyLocation } from "../../../lib/services/collision/jump_graph_types";
import { JumpNode,JumpEdge,JumpNodeKind } from "../../../lib/services/collision/jump_graph_types";
import JumpController from "./jump_controller";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import Frisbee from "../frisbee/frisbee";
import FrisbeeFetchController from "./frisbee_fetch_controller";
import {FetchPlan} from "./fetch_types"

const RETICLE_Y_OFFSET = 0.03;
const OBJECT_OVER_HEIGHT   = 0.55;
const OBJECT_PAST_DISTANCE = 2.5;
const THROW_HOLD_SECONDS = 1.5;

export default class FrisbeeController extends SceneObject {
  private puppy: Puppy;
  private input: InputService;
  private cam: DioramaCamera;
  private reticle: Reticle;
  private frisbee: Frisbee;
  private render_service:RenderService;
  private collision_service:CollisionService;
  private tracking:boolean = false;
  private was_tracking:boolean = false;
  jump_controller:JumpController| null = null;
  fetch_controller:FrisbeeFetchController| null = null;
  
  private last_nav_hit_node_id: string | null = null;
  private last_hit: ReticleHit | null = null;
  private aim_hold_seconds: number = 0;

  private enabled:boolean = false; 

  constructor(key: string, puppy: Puppy, cam: DioramaCamera, reticle: Reticle,frisbee: Frisbee) {
    super(key);
    this.puppy = puppy;
    this.input = Stage.instance().input_service;
    this.render_service = Stage.instance().render_service;
    this.collision_service = Stage.instance().collision_service;
    this.cam = cam;
    this.reticle = reticle;
    this.frisbee = frisbee;
  }

   enable()
   {
      this.enabled = true;
      if(this.fetch_controller)
      {
        this.fetch_controller.enabled = true;
      }
   }

   disable()
   {
      this.enabled = false;
      if(this.fetch_controller)
      {
        this.fetch_controller.enabled = false;
      }
   }
  

  private update_reticle():  ReticleHit | null{
    const pointer = this.input.current.pointer;
    const should_track = this.input.is_primary_down() && !this.input.is_pinching();

    if (!should_track) {
      this.reticle.set_visible(false);
      return null;
    }

    const canvas = this.render_service.canvas;
    if (!canvas) {
      this.reticle.set_visible(false);
      return null;
    }

      
    const ray = this.cam.get_screen_ray(
      pointer.x,
      pointer.y,
      canvas.width,
      canvas.height
    );

    const hit = find_reticle_hit(ray, this.collision_service);

    if (!hit) {
      this.reticle.set_visible(false);
      return null;
    }

    this.reticle.set_visible(true);
   
    this.reticle.set(hit.kind,
      hit.point[0],
      hit.point[1] + RETICLE_Y_OFFSET,
      hit.point[2]
    );

     return hit;
  }


private target_from_hit(hit: { kind: "ground" | "nav"; point: Float32Array }): Float32Array
 {
    if (hit.kind === "ground") {
    return new Float32Array([hit.point[0], 0, hit.point[2]]);
  }
  // Nav reticle: walk to the base of that spot (ground Y = 0).
  return new Float32Array([hit.point[0], 0, hit.point[2]]);
}

async build(device: GPUDevice, format: GPUTextureFormat, shader_service: ShaderService,resource_service: ResourceService): Promise<void> 
{
        super.build(device,format,shader_service,resource_service);
        this.jump_controller = new JumpController(
            "jump_c",
            this.puppy,
            this.collision_service.jump_graph
          );

          this.add_child(this.jump_controller);

        this.fetch_controller = new FrisbeeFetchController(
          "frisbee_fetch",
          this.puppy,
          this.frisbee,
          this.jump_controller,
          this.collision_service.jump_graph
        );
        this.add_child(this.fetch_controller);
}

  // --- helpers -----------------------------------------------------------------

private handle_press_or_hold(hit: ReticleHit): void {
  if (!this.jump_controller) return;

  this.last_hit = hit;

  if (hit.kind === "nav") {
    const node = this.collision_service.jump_graph
      .find_object_node_under_world_point(hit.point, 0.5);
    if (node) {
      this.last_nav_hit_node_id = node.id;
      this.jump_controller.set_active_target(node.id);
    }
    return;
  }

  // hit.kind === "ground"
  const location = this.jump_controller.get_location();

  if (location.kind === "object") {
    this.jump_controller.set_ground_intent(hit.point);
    return;
  }

  this.jump_controller.set_active_target(null);
  this.jump_controller.set_ground_intent(null);

  const target = this.target_from_hit(hit);
  if (!this.tracking) {
    this.tracking = true;
    this.puppy.navigator.move_to_point(target, "aggressive", 0.25);
  } else {
    this.puppy.navigator.update_target_position(target);
  }
}


private fire_ground_throw(start: Float32Array, point: Float32Array): void {
  const dx = point[0] - start[0];
  const dz = point[2] - start[2];
  const horizontal = Math.hypot(dx, dz);
  const duration = Math.max(0.8, Math.min(1.8, 0.6 + horizontal * 0.18));

  const plan: FetchPlan = {
    target_kind: "ground_point",
    flight_duration: duration,
    pass_t: null,
    pass_point: null,
    landing_xz: new Float32Array([point[0], 0, point[2]]),
    target_node_id: null,
  };

  this.fetch_controller!.begin_planning(plan, () => {
    this.puppy.detach_child(this.frisbee);
    this.frisbee.throw_to(
      start,
      { kind: "ground_point", point },
      duration
    );
  });
}

private resolve_over_height(cap_top_y: number): number {
  const base = OBJECT_OVER_HEIGHT;
  const slope = 0.45;       // how much to add per unit of cap height
  return base + slope * cap_top_y;
}

private fire_object_throw(
  start: Float32Array,
  _hit_point: Float32Array,
  node: { id: string; position: Float32Array }
): void {

  const cap_top_y = node.position[1];
  const OVER_HEIGHT = this.resolve_over_height(cap_top_y);


  const horiz_to_cap = Math.hypot(
    node.position[0] - start[0],
    node.position[2] - start[2]
  );
  const horiz_total = horiz_to_cap + OBJECT_PAST_DISTANCE;
  const duration = Math.max(0.8, Math.min(1.8, 0.6 + horiz_total * 0.18));

  const cap_top = new Float32Array([
    node.position[0],
    node.position[1],
    node.position[2],
  ]);
  const pass_point = new Float32Array([
    cap_top[0],
    cap_top[1] + OVER_HEIGHT,
    cap_top[2],
  ]);

  const dx = cap_top[0] - start[0];
  const dz = cap_top[2] - start[2];
  const dlen = Math.hypot(dx, dz) || 1;
  const ux = dx / dlen;
  const uz = dz / dlen;
  const landing_xz = new Float32Array([
    cap_top[0] + ux * OBJECT_PAST_DISTANCE,
    0,
    cap_top[2] + uz * OBJECT_PAST_DISTANCE,
  ]);

  const plan: FetchPlan = {
    target_kind: "object_pass",
    flight_duration: duration,
    pass_t: null,
    pass_point,
    landing_xz,
    target_node_id: node.id,
  };

  this.fetch_controller!.begin_planning(plan, () => {
    this.puppy.detach_child(this.frisbee);
    this.frisbee.throw_to(
      start,
      {
        kind: "object_pass",
        node_id: node.id,
        cap_top,
        over_height: OVER_HEIGHT,
        past_distance: OBJECT_PAST_DISTANCE,
      },
      duration
    );

    const pass_secs = this.frisbee.get_pass_point_time_seconds();
    if (pass_secs !== null) {
      this.fetch_controller!.update_pass_t_after_fire(pass_secs / duration);
    }
  });
}

private dispatch_throw(hit: ReticleHit): void {
  const start = this.compute_throw_start_from_camera();

  if (hit.kind === "ground") {
    this.fire_ground_throw(start, hit.point);
    return;
  }

  // Object throw
  const node = this.collision_service.jump_graph
    .find_object_node_under_world_point(hit.point, 0.5);

  if (!node) {
    // Fall back to ground-style throw at the same hit point.
    this.fire_ground_throw(start, hit.point);
    return;
  }

  this.fire_object_throw(start, hit.point, node);
}


private throw_frisbee(hit: ReticleHit): void {
  const start = this.compute_throw_start_from_camera();

  // --- Ground throw -------------------------------------------------------
  if (hit.kind === "ground") {
    const dx = hit.point[0] - start[0];
    const dz = hit.point[2] - start[2];
    const horizontal = Math.hypot(dx, dz);
    const duration = Math.max(0.8, Math.min(1.8, 0.6 + horizontal * 0.18));

    this.puppy.detach_child(this.frisbee);
    this.frisbee.throw_to(
      start,
      { kind: "ground_point", point: hit.point },
      duration
    );
    return;
  }

  // --- Object throw (nav hit) --------------------------------------------
  const node = this.collision_service.jump_graph
    .find_object_node_under_world_point(hit.point, 0.5);

  // Reticle was on nav geometry but no node resolved — fall back to ground throw.
  if (!node) {
    const dx = hit.point[0] - start[0];
    const dz = hit.point[2] - start[2];
    const horizontal = Math.hypot(dx, dz);
    const duration = Math.max(0.8, Math.min(1.8, 0.6 + horizontal * 0.18));

    this.puppy.detach_child(this.frisbee);
    this.frisbee.throw_to(
      start,
      { kind: "ground_point", point: hit.point },
      duration
    );
    return;
  }

  // Duration scaled by distance from launch to the landing point past the cap.
  // We approximate by using XZ to the cap plus past_distance.
  const dx = node.position[0] - start[0];
  const dz = node.position[2] - start[2];
  const horizontal_to_cap = Math.hypot(dx, dz);
  const horizontal_total = horizontal_to_cap + OBJECT_PAST_DISTANCE;
  const duration = Math.max(0.8, Math.min(1.8, 0.6 + horizontal_total * 0.18));

  const cap_top = new Float32Array([
    node.position[0],
    node.position[1],
    node.position[2],
  ]);

  this.puppy.detach_child(this.frisbee);
  this.frisbee.throw_to(
    start,
    {
      kind: "object_pass",
      node_id: node.id,
      cap_top,
      over_height: OBJECT_OVER_HEIGHT,
      past_distance: OBJECT_PAST_DISTANCE,
    },
    duration
  );
}


private compute_throw_start_from_camera(): Float32Array {
  const cam_pos = this.cam.eye;       // real field on Camera
  const cam_tgt = this.cam.target;    // real field on DioramaCamera

  // Forward = unit(target - eye)
  const fx = cam_tgt[0] - cam_pos[0];
  const fy = cam_tgt[1] - cam_pos[1];
  const fz = cam_tgt[2] - cam_pos[2];
  const flen = Math.hypot(fx, fy, fz) || 1;
  const ux = fx / flen;
  const uy = fy / flen;
  const uz = fz / flen;

  // Right = forward × world_up (world_up = +Y) on XZ.
  // (uy * 0 - uz * 1, uz * 0 - ux * 0, ux * 1 - uy * 0)  →  (-uz, 0, ux)
  const rx = -uz;
  const rz =  ux;

  // Offsets in world units. Tune visually.
  const FORWARD_OFFSET = 1.2;   // how far in front of the camera the throw starts
  const SIDE_OFFSET    = 0.4;   // shift to the right so it doesn't cover the center
  const DOWN_OFFSET    = 0.9;   // chest-height drop below the optical axis

  return new Float32Array([
    cam_pos[0] + ux * FORWARD_OFFSET + rx * SIDE_OFFSET,
    cam_pos[1] + uy * FORWARD_OFFSET - DOWN_OFFSET,
    cam_pos[2] + uz * FORWARD_OFFSET + rz * SIDE_OFFSET,
  ]);
}


private update_path_highlight(hit: ReticleHit | null): void {

  if(!this.jump_controller) return;
  const graph = this.collision_service.jump_graph;

  if (!hit || hit.kind !== "nav") {
    graph.set_highlighted_path(null);
    return;
  }

  const node = graph.find_object_node_under_world_point(hit.point, 0.5);
  if (!node) {
    graph.set_highlighted_path(null);
    return;
  }

  const from = this.jump_controller.get_location();
  const path = graph.plan_path(from, node.id);
  graph.set_highlighted_path(path);
}

tick(delta_time: number): void {
  if(!this.enabled) return;
  if (!this.jump_controller || !this.fetch_controller) {
    super.tick(delta_time);
    return;
  }

  // Player locked out during fetch.
  if (this.fetch_controller.is_player_locked()) {
    this.reticle.set_visible(false);
    this.collision_service.jump_graph.set_highlighted_path(null);
    this.last_hit = null;
    this.last_nav_hit_node_id = null;
    this.tracking = false;
    this.aim_hold_seconds = 0;
    super.tick(delta_time);
    return;
  }

  const hit = this.update_reticle();
  const primary_down = this.input.is_primary_down();
  const pinching = this.input.is_pinching();

  // Pinch interrupts aiming. Reset everything quietly.
  if (pinching) {
    this.cancel_aim_silently();
    super.tick(delta_time);
    return;
  }

  // Single-finger aim active: accumulate hold time only while we have a valid hit.
  if (primary_down && hit) {
    this.handle_press_or_hold(hit);
    this.aim_hold_seconds += delta_time;
    this.update_path_highlight(hit);
     const ready = this.aim_hold_seconds >= THROW_HOLD_SECONDS;
     this.reticle.set_ready(ready);  // small method to swap color/scale

    super.tick(delta_time);
    return;
  }

  // Single-finger held but no valid hit (e.g., dragging over empty sky).
  // Keep tracking alive but freeze the timer — drift off geometry shouldn't
  // accumulate aim time.
  if (primary_down && !hit) {
    this.reticle.set_visible(false);
    this.collision_service.jump_graph.set_highlighted_path(null);
    super.tick(delta_time);
    return;
  }

  // Primary up. Two cases:
  //   1) We had been aiming long enough → fire the throw.
  //   2) We hadn't (or never started) → cancel quietly.
  if (this.tracking || this.last_hit) {
    if (this.aim_hold_seconds >= THROW_HOLD_SECONDS && this.last_hit) {
      this.fire_throw_from_last_hit();
    } else {
      this.cancel_aim_silently();
    }
  }

  super.tick(delta_time);
}

private cancel_aim_silently(): void {
  this.tracking = false;
  this.last_hit = null;
  this.last_nav_hit_node_id = null;
  this.aim_hold_seconds = 0;

  this.reticle.set_visible(false);
  this.collision_service.jump_graph.set_highlighted_path(null);

  if (this.jump_controller) {
    this.jump_controller.set_active_target(null);
    this.jump_controller.set_ground_intent(null);
  }
  this.puppy.navigator.request_arrive_here();
}

private fire_throw_from_last_hit(): void {
  const released_hit = this.last_hit;
  this.tracking = false;
  this.last_hit = null;
  this.last_nav_hit_node_id = null;
  this.aim_hold_seconds = 0;

  this.reticle.set_visible(false);
  this.collision_service.jump_graph.set_highlighted_path(null);

  if (this.jump_controller) {
    this.jump_controller.set_active_target(null);
    this.jump_controller.set_ground_intent(null);
  }
  this.puppy.navigator.request_arrive_here();

  if (released_hit) this.dispatch_throw(released_hit);
}



  private log_path(dest: JumpNode, path: JumpEdge[] | null): void {
  if (!path) {
    console.log(`[plan] no path to ${dest.id} at (${dest.position[0].toFixed(2)}, ${dest.position[1].toFixed(2)}, ${dest.position[2].toFixed(2)})`);
    return;
  }
  if (path.length === 0) {
    console.log(`[plan] already at ${dest.id}`);
    return;
  }

  console.log(`[plan] -> ${dest.id} (${path.length} step${path.length === 1 ? "" : "s"})`);
  let total_h = 0;
  let total_v = 0;
  for (const e of path) {
    console.log(`        ${e.from.padEnd(10)} -> ${e.to.padEnd(10)}  h=${e.horizontal.toFixed(2)}  v=${e.vertical.toFixed(2)}  ${e.anim}`);
    total_h += e.horizontal;
    total_v += Math.max(0, e.vertical);
  }
  console.log(`        totals  h=${total_h.toFixed(2)}  up=${total_v.toFixed(2)}`);
}
}